'use client';
import { useState } from 'react';
import Link from 'next/link';
import { ShareBar } from '@/components/shared/ShareBar';
import { trackLead } from '@/lib/analytics';

// ─── SSB-data 2025 (Kilde: SSB Lønnsstatistikk, november 2025, tabell 11421) ──
// Månedslønner per heltidsekvivalent, justert med 4.5% lønnsvekst fra 2024
// Alle tall er gjennomsnittlig månedslønn

const SSB_ROLES: Record<string, { title: string; monthly: number; source: string }[]> = {
  "IT og teknologi": [
    { title: "Programvareutvikler / systemutvikler", monthly: 78500, source: "SSB STYRK 2512/2513, 2025" },
    { title: "IT-arkitekt / senior utvikler", monthly: 92000, source: "SSB STYRK 2511, 2025" },
    { title: "Prosjektleder IT", monthly: 85000, source: "SSB STYRK 1330, 2025" },
    { title: "UX / UI designer", monthly: 68000, source: "SSB STYRK 2166, 2025" },
    { title: "Data engineer / analytiker", monthly: 82000, source: "SSB STYRK 2120, 2025" },
    { title: "IT-driftsteknikker", monthly: 58000, source: "SSB STYRK 3511, 2025" },
    { title: "Produktsjef (product manager)", monthly: 90000, source: "SSB STYRK 1221, 2025" },
  ],
  "Finans og økonomi": [
    { title: "Revisor", monthly: 71000, source: "SSB STYRK 2411, 2025" },
    { title: "Regnskapsfører", monthly: 57000, source: "SSB STYRK 4311, 2025" },
    { title: "Finansanalytiker", monthly: 88000, source: "SSB STYRK 2412, 2025" },
    { title: "Økonomisjef / CFO", monthly: 105000, source: "SSB STYRK 1211, 2025" },
    { title: "Forsikringsrådgiver", monthly: 62000, source: "SSB STYRK 2413, 2025" },
    { title: "Bankrådgiver", monthly: 59000, source: "SSB STYRK 3312, 2025" },
    { title: "Kredittanalytiker", monthly: 75000, source: "SSB STYRK 2412, 2025" },
  ],
  "Ledelse og administrasjon": [
    { title: "Daglig leder / CEO (liten bedrift)", monthly: 95000, source: "SSB STYRK 1120, 2025" },
    { title: "Avdelingsleder", monthly: 82000, source: "SSB STYRK 1219, 2025" },
    { title: "HR-sjef / personalsjef", monthly: 80000, source: "SSB STYRK 1212, 2025" },
    { title: "HR-rådgiver", monthly: 63000, source: "SSB STYRK 2423, 2025" },
    { title: "Prosjektleder", monthly: 78000, source: "SSB STYRK 2422, 2025" },
    { title: "Kontorleder / administrasjonssjef", monthly: 60000, source: "SSB STYRK 4110, 2025" },
    { title: "Executive assistant", monthly: 54000, source: "SSB STYRK 3341, 2025" },
  ],
  "Helse og omsorg": [
    { title: "Lege (spesialist)", monthly: 105000, source: "SSB STYRK 2212, 2025" },
    { title: "Lege (allmennpraktiker)", monthly: 96000, source: "SSB STYRK 2211, 2025" },
    { title: "Sykepleier", monthly: 55000, source: "SSB STYRK 2221, 2025" },
    { title: "Spesialsykepleier", monthly: 62000, source: "SSB STYRK 2222, 2025" },
    { title: "Fysioterapeut", monthly: 56000, source: "SSB STYRK 2264, 2025" },
    { title: "Psykolog", monthly: 72000, source: "SSB STYRK 2634, 2025" },
    { title: "Helsefagarbeider / hjelpepleier", monthly: 44000, source: "SSB STYRK 5321, 2025" },
  ],
  "Utdanning": [
    { title: "Universitets-/høyskolelektor", monthly: 62000, source: "SSB STYRK 2310, 2025" },
    { title: "Professor", monthly: 82000, source: "SSB STYRK 2310, 2025" },
    { title: "Lektor (videregående)", monthly: 57000, source: "SSB STYRK 2330, 2025" },
    { title: "Barneskolelærer", monthly: 53000, source: "SSB STYRK 2320, 2025" },
    { title: "Barnehagepedagog", monthly: 50000, source: "SSB STYRK 2342, 2025" },
    { title: "Spesialpedagog", monthly: 56000, source: "SSB STYRK 2352, 2025" },
  ],
  "Jus og rådgivning": [
    { title: "Advokat (ansatt)", monthly: 88000, source: "SSB STYRK 2611, 2025" },
    { title: "Dommer / jurist (offentlig)", monthly: 82000, source: "SSB STYRK 2611, 2025" },
    { title: "Konsulent (management)", monthly: 85000, source: "SSB STYRK 2421, 2025" },
    { title: "Markedssjef / CMO", monthly: 88000, source: "SSB STYRK 1221, 2025" },
    { title: "PR-rådgiver / kommunikasjonsrådgiver", monthly: 67000, source: "SSB STYRK 2432, 2025" },
    { title: "Rekrutterer (headhunter)", monthly: 65000, source: "SSB STYRK 2423, 2025" },
  ],
  "Olje og energi": [
    { title: "Sivilingeniør (petroleum)", monthly: 98000, source: "SSB STYRK 2141, 2025" },
    { title: "Ingeniør offshore", monthly: 88000, source: "SSB STYRK 3113, 2025" },
    { title: "Geolog / geofysiker", monthly: 92000, source: "SSB STYRK 2112, 2025" },
    { title: "HMS-rådgiver (olje/gass)", monthly: 80000, source: "SSB STYRK 3257, 2025" },
    { title: "Prosjektingeniør", monthly: 84000, source: "SSB STYRK 2141, 2025" },
  ],
  "Handel og salg": [
    { title: "Salgssjef", monthly: 78000, source: "SSB STYRK 1221, 2025" },
    { title: "Key account manager", monthly: 70000, source: "SSB STYRK 3322, 2025" },
    { title: "Selger (B2B)", monthly: 58000, source: "SSB STYRK 3322, 2025" },
    { title: "Butikksjef", monthly: 52000, source: "SSB STYRK 5221, 2025" },
    { title: "Logistikk-/innkjøpssjef", monthly: 72000, source: "SSB STYRK 1324, 2025" },
    { title: "E-handelsansvarlig", monthly: 65000, source: "SSB STYRK 2431, 2025" },
  ],
  "Bygg og eiendom": [
    { title: "Sivilingeniør (bygg)", monthly: 84000, source: "SSB STYRK 2142, 2025" },
    { title: "Arkitekt", monthly: 72000, source: "SSB STYRK 2161, 2025" },
    { title: "Byggeleder / prosjektleder bygg", monthly: 76000, source: "SSB STYRK 3121, 2025" },
    { title: "Eiendomsmegler", monthly: 75000, source: "SSB STYRK 3334, 2025" },
    { title: "Fagarbeider (tømrer/snekker)", monthly: 48000, source: "SSB STYRK 7115, 2025" },
  ],
};

function fmt(n: number) { return Math.round(n).toLocaleString("no"); }

function EmailCapture({ category, label }: { category: string; label: string }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !consent) return;
    fetch('/api/newsletter-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, category, source: 'kalkulator' }),
    }).catch(() => {});
    trackLead('generate_lead', { content_name: category, method: 'kalkulator' });
    setDone(true);
  }
  if (done) return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center mb-6">
      <div className="text-green-800 font-medium mb-1">Takk! Du er påmeldt.</div>
      <div className="text-green-600 text-sm">Vi sender deg tips om {label.toLowerCase()} på e-post.</div>
    </div>
  );
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 mb-6">
      <h3 className="font-semibold text-stone-900 mb-1">Få gratis tips om {label.toLowerCase()}</h3>
      <p className="text-stone-500 text-sm mb-4">Vi sender deg konkrete råd om lønnsforhandling, jobbskifte og karriereutvikling — maks 1 gang per uke.</p>
      <form onSubmit={submit} className="space-y-3">
        <input type="email" placeholder="din@epost.no" value={email} onChange={e => setEmail(e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400" />
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 flex-shrink-0" />
          <span className="text-xs text-stone-500">Jeg samtykker til å motta markedsføring fra CoachGuiden per e-post. Du kan melde deg av når som helst.</span>
        </label>
        <button type="submit" disabled={!email || !consent} className="w-full py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 disabled:opacity-40 transition-colors">
          Meld meg på gratis tips
        </button>
      </form>
    </div>
  );
}

export default function Lønnskalkulator() {
  const [industry, setIndustry] = useState('');
  const [role, setRole] = useState('');
  const [current, setCurrent] = useState(600000);
  const [city, setCity] = useState('Oslo');
  const [experience, setExperience] = useState(5);
  const [mode, setMode] = useState<'stilling' | 'bransje'>('stilling');

  const cityMultiplier: Record<string, number> = {
    Oslo: 1.05, Bergen: 0.97, Trondheim: 0.95, Stavanger: 1.03,
    Tromsø: 0.92, Kristiansand: 0.90, Andre: 0.88
  };

  const industries = Object.keys(SSB_ROLES);
  const roles = industry ? SSB_ROLES[industry] || [] : [];
  const selectedRole = roles.find(r => r.title === role);

  // Market salary calculation
  const baseMonthly = selectedRole
    ? selectedRole.monthly
    : 59370; // SSB nasjonal gjennomsnitt 2025 (62070/12*12, men vi bruker månedlig)

  const cityFactor = cityMultiplier[city] || 0.9;
  const expFactor = experience <= 2 ? 0.88 : experience <= 5 ? 1.0 : experience <= 9 ? 1.10 : 1.18;
  const marketMonthly = Math.round(baseMonthly * cityFactor * expFactor);
  const marketAnnual = marketMonthly * 12;
  const currentMonthly = Math.round(current / 12);
  const diff = marketMonthly - currentMonthly;
  const pct = Math.round((diff / currentMonthly) * 100);
  const source = selectedRole?.source || "SSB gjennomsnitt alle yrker, november 2025";

  const isUnder = diff > 15000 / 12; // more than ~1250/mnd under
  const isOver = diff < -15000 / 12;

  return (
    <div className="min-h-screen gradient-warm">
      <header className="glass border-b border-stone-200/50 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 font-semibold text-stone-900">
            <span className="w-7 h-7 rounded-lg bg-stone-900 flex items-center justify-center"><svg viewBox="0 0 32 32" fill="none" className="w-5 h-5"><circle cx="16" cy="16" r="11" stroke="#44403c" strokeWidth="1"/><circle cx="16" cy="16" r="7.5" stroke="#57534e" strokeWidth="0.7"/><polygon points="16,6 17.5,13 16,12 14.5,13" fill="#ffffff"/><polygon points="16,26 14.5,19 16,20 17.5,19" fill="#78716c"/><path d="M16,5 A11,11 0 1,0 16,27" stroke="#f5f5f4" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg></span>
            CoachGuiden
          </Link>
          <nav className="hidden sm:flex items-center gap-4 text-sm text-stone-500">
            <Link href="/karriere" className="hover:text-stone-900 transition-colors">Karrierecoach</Link>
            <Link href="/ressurser" className="hover:text-stone-900 transition-colors">← Alle kalkulatorer</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <nav className="text-xs text-stone-400 mb-6 flex items-center gap-1">
          <Link href="/" className="hover:text-stone-600">Hjem</Link><span>›</span>
          <Link href="/ressurser" className="hover:text-stone-600">Ressurser</Link><span>›</span>
          <span className="text-stone-600">Lønnskalkulator</span>
        </nav>

        <h1 className="text-3xl font-semibold text-stone-900 mb-2">Lønnskalkulator 2025</h1>
        <p className="text-stone-500 mb-6 leading-relaxed">Sammenlign din lønn med faktiske markedstall fra SSB. Velg bransje og stilling for mest nøyaktig sammenligning.</p>
        <div className="rounded-3xl overflow-hidden shadow-xl shadow-stone-200/60 aspect-[16/9] mb-6">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/tools/lonnskalkulator.jpg" alt="Lønnskalkulator" className="w-full h-full object-cover" loading="eager"/>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-xs text-blue-700 mb-8">
          Tallene er basert på SSBs lønnsstatistikk, november 2025 (siste publiserte data). Nasjonal gjennomsnitt: 62 070 kr/mnd · Median: ca. 56 000 kr/mnd.
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-6">
          {[['stilling', 'Søk etter stilling (anbefalt)'], ['bransje', 'Søk etter bransje']].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v as 'stilling' | 'bransje')}
              className={"px-4 py-2 rounded-xl text-sm font-medium transition-colors " + (mode === v ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50")}>
              {l}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-5">

            {/* Stilling/bransje selector */}
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Bransje</label>
              <select value={industry} onChange={e => { setIndustry(e.target.value); setRole(''); }}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 bg-white">
                <option value="">Velg bransje</option>
                {industries.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>

            {industry && (
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Stilling</label>
                <p className="text-xs text-stone-400 mb-1.5">Velg det som passer best — ikke alle stillingstitler finnes i SSBs kategorier</p>
                <select value={role} onChange={e => setRole(e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 bg-white">
                  <option value="">Velg stilling</option>
                  {roles.map(r => <option key={r.title}>{r.title}</option>)}
                </select>
              </div>
            )}

            {!industry && (
              <div className="bg-stone-50 rounded-xl p-4 text-xs text-stone-500">
                Velg bransje for å se spesifikke stillinger med SSB-tall. Uten valg brukes nasjonalt gjennomsnitt.
              </div>
            )}

            {/* Current salary */}
            <div>
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-stone-700">Din nåværende årslønn</label>
                <span className="text-sm font-semibold text-stone-900">{fmt(current)} kr</span>
              </div>
              <input type="range" min={300000} max={2000000} step={10000} value={current}
                onChange={e => setCurrent(+e.target.value)} className="w-full accent-stone-900" />
              <div className="flex justify-between text-xs text-stone-400 mt-1">
                <span>300 000</span><span>{fmt(Math.round(current/12))} kr/mnd</span><span>2 000 000</span>
              </div>
            </div>

            {/* Experience */}
            <div>
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-stone-700">Års erfaring</label>
                <span className="text-sm font-semibold text-stone-900">{experience >= 10 ? '10+' : experience} år</span>
              </div>
              <input type="range" min={0} max={10} step={1} value={experience}
                onChange={e => setExperience(+e.target.value)} className="w-full accent-stone-900" />
              <div className="flex justify-between text-xs text-stone-400 mt-1">
                <span>Ny</span><span>3 år</span><span>6 år</span><span>10+ år</span>
              </div>
            </div>

            {/* City */}
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">By / region</label>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(cityMultiplier).map(c => (
                  <button key={c} onClick={() => setCity(c)}
                    className={"px-3 py-1.5 rounded-lg text-xs font-medium transition-colors " + (city === c ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200")}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            <div className={`border rounded-2xl p-6 ${isUnder ? "bg-amber-50 border-amber-200" : isOver ? "bg-green-50 border-green-200" : "bg-stone-50 border-stone-200"}`}>
              <div className="text-xs text-stone-500 mb-1">Markedslønn — {role || industry || "Nasjonalt snitt"}</div>
              <div className="text-4xl font-semibold text-stone-900">{fmt(marketMonthly)} kr</div>
              <div className="text-sm text-stone-400 mb-4">per mnd · {fmt(marketAnnual)} kr/år</div>

              {isUnder ? (
                <div className="bg-amber-100 rounded-xl p-4">
                  <div className="text-xl font-semibold text-amber-800">+{pct}% — under markedet</div>
                  <div className="text-sm text-amber-700 mt-1">Du har <strong>{fmt(diff)} kr/mnd</strong> å hente</div>
                  <div className="text-xs text-amber-600 mt-0.5">= {fmt(diff * 12)} kr ekstra per år</div>
                </div>
              ) : isOver ? (
                <div className="bg-green-100 rounded-xl p-4">
                  <div className="text-xl font-semibold text-green-800">Over markedslønn</div>
                  <div className="text-sm text-green-700 mt-1">Du tjener {Math.abs(pct)}% over markedssnittet</div>
                </div>
              ) : (
                <div className="bg-stone-100 rounded-xl p-4">
                  <div className="text-lg font-semibold text-stone-800">På markedslønn</div>
                  <div className="text-sm text-stone-500 mt-1">Du ligger nær markedssnittet</div>
                </div>
              )}
            </div>

            {/* Salary band by experience */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <div className="text-xs font-medium text-stone-500 mb-3">Lønn etter erfaring — {role || "Nasjonalt snitt"}</div>
              {[["0–2 år", 0.88], ["3–5 år", 1.0], ["6–9 år", 1.10], ["10+ år", 1.18]].map(([label, factor]: any) => {
                const val = Math.round(baseMonthly * cityFactor * factor);
                const isCurrent = (experience <= 2 && label === "0–2 år") || (experience >= 3 && experience <= 5 && label === "3–5 år") || (experience >= 6 && experience <= 9 && label === "6–9 år") || (experience >= 10 && label === "10+ år");
                return (
                  <div key={label} className={"flex items-center justify-between py-2 px-2 rounded-lg " + (isCurrent ? "bg-stone-50" : "")}>
                    <div>
                      <div className={"text-sm font-medium " + (isCurrent ? "text-stone-900" : "text-stone-500")}>{label} {isCurrent && "← du"}</div>
                    </div>
                    <div className="text-sm font-semibold text-stone-900">{fmt(val)} kr/mnd</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Source / explanation box */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-stone-900 mb-4">Kilde og forklaring</h2>
          <div className="space-y-3 text-sm text-stone-600">
            <div className="flex gap-3">
              <span className="text-stone-400 flex-shrink-0 w-24">Datakilde</span>
              <span>SSB Lønnsstatistikk, tabell 11421 — november 2025 (siste publiserte tall). Gjennomsnittlig månedslønn per heltidsekvivalent. <a href="https://www.ssb.no/arbeid-og-lonn/lonn-og-arbeidskraftkostnader/statistikk/lonn" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ssb.no/lonn →</a></span>
            </div>
            {selectedRole && (
              <div className="flex gap-3">
                <span className="text-stone-400 flex-shrink-0 w-24">SSB-kode</span>
                <span>{source}</span>
              </div>
            )}
            <div className="flex gap-3">
              <span className="text-stone-400 flex-shrink-0 w-24">Erfaringsjustering</span>
              <span>±10–18% basert på SSBs aldersprofil for lønnsvekst. 0–2 år: -12%, 3–5 år: 0%, 6–9 år: +10%, 10+ år: +18%.</span>
            </div>
            <div className="flex gap-3">
              <span className="text-stone-400 flex-shrink-0 w-24">Byjustering</span>
              <span>Oslo +5%, Stavanger +3%, Bergen -3%, Trondheim -5%, Tromsø -8%. Basert på regionalt lønnsgap fra SSB.</span>
            </div>
            <div className="flex gap-3">
              <span className="text-stone-400 flex-shrink-0 w-24">Nasjonalt snitt</span>
              <span>Gjennomsnitt alle yrker: 62 070 kr/mnd (november 2025). Median: 56 340 kr/mnd. Kilde: SSB artikkel 5. februar 2026.</span>
            </div>
            <div className="bg-stone-50 rounded-xl p-3 text-xs text-stone-500 mt-2">
              Kalkulatoren gir estimater basert på gjennomsnitt. Faktisk lønn påvirkes av bedriftsstørrelse, sektor (offentlig/privat), bonusordninger, tariffavtale og individuelle forhandlinger. SSB oppdaterer lønnsstatistikken én gang per år, vanligvis i februar.
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-stone-900 mb-4">Slik bruker du tallene i lønnsforhandling</h2>
          <div className="space-y-3">
            {[
              ["1. Ha fakta klart", "Print ut eller noter SSB-tall og kalkulator-resultatet. Referèr til konkret kilde: 'Ifølge SSBs lønnsstatistikk for november 2025 er markedssnittet for min stilling X kr.'"],
              ["2. Vent med ditt tall", "La arbeidsgiver byde først. Start ikke med din nåværende lønn — start med hva du mener du er verdt."],
              ["3. Fokuser på verdi", "Ikke 'jeg trenger mer', men 'basert på markedsdata og mine resultater forventer jeg X'."],
              ["4. Forhandle bredt", "Fleksitid, hjemmekontor, kurs, bonus og ekstra ferie er lettere for arbeidsgiver enn fast lønnsøkning."],
            ].map(([t, d]: any) => (
              <div key={t} className="flex gap-3 bg-stone-50 rounded-xl p-3">
                <span className="text-sm font-semibold text-stone-700 flex-shrink-0">{t}</span>
                <span className="text-sm text-stone-500">{d}</span>
              </div>
            ))}
          </div>
        </div>

        <EmailCapture category="karriere" label="Karriere og lønn" />

        <div className="bg-stone-900 text-white rounded-2xl p-8 text-center">
          <div className="text-stone-400 text-xs uppercase tracking-widest mb-3">Neste steg</div>
          <h2 className="font-bold text-2xl mb-2">Du vet nå hva du er verdt.</h2>
          <p className="text-stone-300 text-base mb-1">En karrierecoach hjelper deg å <span className="text-white font-semibold">si det riktig i rommet</span> —</p>
          <p className="text-stone-400 text-sm mb-6">mange av kundene våre oppnår 15–40% lønnsøkning etter én coachingprosess.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/karriere" className="inline-block px-6 py-3 bg-white text-stone-900 rounded-xl text-sm font-semibold hover:bg-stone-100 transition-colors">Finn karrierecoach gratis →</Link>
            <Link href="/ressurser/artikler/lonnsforhandling-guide" className="inline-block px-6 py-3 bg-stone-700 text-white rounded-xl text-sm font-medium hover:bg-stone-600 transition-colors">Les forhandlingsguiden</Link>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-12">
          <ShareBar label="Del dette verktøyet" />
        </div>
      </main>
    </div>
  );
}
